import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { CurrencyDisplay } from "@/components/currency/CurrencyDisplay";
import { DailyReward } from "@/components/currency/DailyReward";
import appSound from '@/services/app-sound-service';
import { audioCache } from "@/services/audio-cache";
import NotificationBell from "@/components/ui/notification-bell";
import { AchievementsDialog } from "@/components/ui/achievements-dialog";
import { useNewAchievements } from "@/hooks/use-new-achievements";
import { useAwardNotifications } from "@/hooks/use-award-notifications";
import { AwardNotificationDialog } from "@/components/award/AwardNotificationDialog";
import { PullToRefresh } from "@/components/ui/pull-to-refresh";
import { MatchCard } from "@/components/game/match-card";
import { AnnouncementBanner, useAnnouncementBanner } from "@/components/announcement-banner";

import {
  Card,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useLocation } from "wouter";
import { useState, useEffect, useRef } from "react";
import { useQueueStore } from "@/stores/queue-store";
import { Capacitor } from "@capacitor/core";
import { useWebSocket } from "@/providers/websocket-provider";
import useGameNotifications from "@/hooks/use-game-notifications";
import { GameNotifications } from "@/components/ui/game-notifications";
import {
  Loader2,
  Users,
  Plus,
  LogIn,
  Tv,
  Monitor,
  Settings,
  PlayIcon,
  GamepadIcon,
  BookOpen,
  ClockIcon,
  Clock,
  Swords,
  Trophy,
  Shield,
  Gift,
  Backpack,
  ShipWheel,
  ShieldCheck,
  ShieldPlus,
  Search,
  Ribbon,
  Cog,
  Award,
} from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { apiRequest } from "@/lib/queryClient";
import { type GameRoom } from "@shared/schema";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { gameSettings } from "@shared/game-settings";
import { AnimatePresence, motion } from "framer-motion";
import { HowToPlay } from "@/components/ui/how-to-play";
import { AppLayout } from "@/components/ui/app-layout";
import { CreateGameDialog } from "@/components/game/create-game-dialog";
import { EnhancedHeader } from "@/components/ui/enhanced-header";
import { LearnTabContent } from "@/components/tutorial/LearnTabContent"; // Import the new component

type Tab = "play" | "games" | "learn" | "history";

const playCardSound = () => {
  // Force user interaction
  audioCache.markUserInteraction();
  // Play button sound
  appSound.playButtonSound();
}

export default function GamesPage() {
  const queryClient = useQueryClient();
  const { user, token } = useAuth();
  const [_, setLocation] = useLocation();
  const [selectedTab, setSelectedTab] = useState<Tab>("play");
  const { toast } = useToast();
  const { isInQueue, isInQuickQueue, setIsInQueue, setIsInQuickQueue, setIsSearching } = useQueueStore();
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState("all");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [showHowToPlay, setShowHowToPlay] = useState(false);
  const [isPracticeLoading, setIsPracticeLoading] = useState(false);
  const [matchSearchTerm, setMatchSearchTerm] = useState("");
  const [matchTypeFilter, setMatchTypeFilter] = useState("all");
  const [matchResultFilter, setMatchResultFilter] = useState("all");

  // Game notification system
  const { notifications, showNotification, removeNotification } = useGameNotifications();
  
  // Achievement dialog system
  const { newAchievements, showDialog, closeDialog } = useNewAchievements();
  
  // Award notification system
  const { 
    pendingAwards, 
    hasUnclaimed, 
    isDialogOpen: isAwardDialogOpen, 
    setIsDialogOpen: setIsAwardDialogOpen,
    handleClaimAll 
  } = useAwardNotifications();

  // Add this function to your GamesPage component
  const isDailyRewardClaimed = () => {
    const lastClaimedDate = localStorage.getItem("lastDailyRewardClaim");
    if (lastClaimedDate) {
      const today = new Date().toDateString();
      return lastClaimedDate === today;
    }
    return false;
  };
  
  // Announcements system
  const { loadAnnouncements } = useAnnouncementBanner();
  
  // WebSocket setup
  const wsRef = useRef<WebSocket | null>(null);
  const webSocketService = useWebSocket();
  
  // Ref to track if announcements have been loaded
  const announcementsLoadedRef = useRef(false);

  // Load announcements when component mounts
  useEffect(() => {
    // Only attempt to load announcements if user is logged in and not already loaded
    if (user && token && !announcementsLoadedRef.current) {
      loadAnnouncements();
      announcementsLoadedRef.current = true;
    }
  }, [user, token, loadAnnouncements]);

  // Listen for game notifications from the WebSocketService
  useEffect(() => {
    // Add event listener for game-notification events from WebSocketService
    const handleGameNotification = (event: any) => {
      const { title, message, type, timeout } = event.detail;
      showNotification(title, message, type, timeout);
    };

    window.addEventListener("game-notification", handleGameNotification);

    // Clean up
    return () => {
      window.removeEventListener("game-notification", handleGameNotification);
    };
  }, [showNotification]);

  // Check queue status when the component mounts using global WebSocket service
  useEffect(() => {
    if (token) {
      console.log(
        "[Matchmaking] Checking queue status on component mount using global service",
      );
      // Use centralized queue status checking
      webSocketService.checkQueueStatus();
    }
  }, [token, webSocketService]);

  // Initialize WebSocket connection
  useEffect(() => {
    // Close any existing connection
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.close();
    }

    // Create WebSocket URL with Capacitor-aware logic
    const wsUrl = Capacitor.isNativePlatform()
      ? `${getWebSocketProtocol(import.meta.env.VITE_SERVER_URL ?? window.location.origin)}//${(new URL(import.meta.env.VITE_SERVER_URL ?? window.location.origin)).host}/ws`
      : `${getWebSocketProtocol(window.location.origin)}//${window.location.host}/ws`;

    console.log("[WebSocket] Creating new connection to:", wsUrl);
    const newWs = new WebSocket(wsUrl);

    newWs.onopen = () => {
      console.log("[WebSocket] Connection established");

      // Check queue status as soon as the connection is established
      if (token) {
        console.log(
          "[Matchmaking] Checking queue status after connection established",
        );
        setTimeout(() => {
          if (newWs.readyState === WebSocket.OPEN) {
            newWs.send(
              JSON.stringify({
                type: "CHECK_QUEUE_STATUS",
                token: token,
              }),
            );
          }
        }, 500); // Small delay to ensure connection is fully established
      }
    };

    newWs.onclose = () => {
      console.log("[WebSocket] Connection closed");
    };

    newWs.onerror = (error) => {
      console.error("[WebSocket] Error:", error);
    };

    newWs.onmessage = async (event) => {
      console.log("[WebSocket] Raw message received:", event.data);

      let data;
      try {
        data = JSON.parse(event.data);
        console.log("[WebSocket] Parsed message:", {
          type: data.type,
          payload: data,
        });
      } catch (error) {
        console.error("[WebSocket] Failed to parse message:", error);
        return;
      }

      const matchToken = useQueueStore.getState().matchToken;
      switch (data.type) {
        case "QUEUE_JOIN_SUCCESS":
          // Only set ranked queue state
          if(isInQuickQueue){
            // Only set quick match state
            console.log("[Matchmaking] Successfully joined quick match queue");
            setIsInQueue(false);
            break;          
          }
          //console.log("[Matchmaking] Successfully joined ranked queue");
          //setIsInQueue(true);
          setIsSearching(true);
          break;

        case "QUICK_MATCH_JOIN_SUCCESS":
          // Only set quick match state
          console.log("[Matchmaking] Successfully joined quick match queue");
          setIsInQuickQueue(true);
          break;

        case "QUEUE_LEAVE_SUCCESS":
          // Only reset ranked queue state
          setIsInQueue(false);
          setIsSearching(false);
          break;

        case "QUICK_MATCH_LEAVE_SUCCESS":
          // Only reset quick match state
          setIsInQuickQueue(false);
          break;

        case "MATCH_FOUND":
          // For ranked matches
          if (data.type === "ranked") {
            setIsSearching(false);
            setIsInQueue(false);
          }
          break;

        case "QUICK_MATCH_FOUND":
          // For quick matches
          setIsInQuickQueue(false);
          setIsSearching(false);
          break;

        case "MATCH_REJECTED":
          if (data.type === "ranked") {
            setIsSearching(false);
            setIsInQueue(false);
          }
          break;

        case "QUICK_MATCH_REJECTED":
          setIsInQuickQueue(false);
          break;

        case "GAME_CREATED":
          // Reset appropriate queue state based on match type
          if (data.isQuickMatch) {
            setIsInQuickQueue(false);
          } else {
            setIsInQueue(false);
            setIsSearching(false);
          }
          
          if (data.gameId) {
            setLocation(`/game/${data.gameId}`);
          }
          break;

        case "QUEUE_STATUS_RESPONSE":
          console.log("[Matchmaking] Received queue status:", data);
          // Update UI based on server response
          setIsInQueue(data.inQueue === true);
          if (data.inQueue === true) {
            //showNotification(
              //"Queue Status",
              //"You are still in the matchmaking queue",
              //"info",
              //3000,
            //);
          }
          break;
      }
    };

    wsRef.current = newWs;

    // Clean up on unmount
    return () => {
      console.log("[WebSocket] Cleaning up connection");
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [
    token,
    user,
    showNotification,
    setLocation,
    setIsInQueue,
    setIsSearching,
  ]);

  // NOTE: The match accepting/rejecting functionality has been moved to the global MatchDialog component

  const { data: gameRooms, isLoading } = useQuery({
    queryKey: ["game-rooms"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/game-rooms");
      if (!res.ok) throw new Error("Failed to fetch games");
      return res.json();
    },
  });

  const { data: userMatches, isLoading: isLoadingMatches } = useQuery({
    queryKey: ["matchHistory", user?.id],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/profile/history/${user?.id}`);
      if (!res.ok) throw new Error("Failed to fetch match history");
      return res.json();
    },
    enabled: !!user?.id,
  });

  // Fetch XP data separately
  const { data: xpData } = useQuery({
    queryKey: ["profile-xp", user?.id],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/profile/xp/${user?.id}`);
      if (!res.ok) throw new Error("Failed to fetch XP data");
      return res.json();
    },
    enabled: !!user?.id,
  });

  const currentLevel = (
    <div className="flex items-center gap-2">
      
         <Shield className="h-4 w-4 text-primary" />
      {` Level ${xpData?.level}`}
    </div>
  );

  const userWelcome = (
    <div className="flex items-center gap-2">
      {currentLevel}
      Hi, {user?.username} 
    </div>
  )




  const handleJoinGame = async (id: string) => {
    if (!id.trim()) return;

    // Check if user is verified before allowing to join a game
    if (!user?.isVerified) {
      toast({
        title: "Verification Required",
        description: "You need to verify your email address to join games.",
        variant: "destructive",
        action: (
          <ToastAction
            altText="Verify"
            onClick={() => setLocation("/user-settings")}
          >
            Verify
          </ToastAction>
        ),
      });
      return;
    }

    try {
      const res = await apiRequest("POST", `/api/game-rooms/${id}/join`);
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message ?? "Failed to join game");
      }
      await queryClient.invalidateQueries({ queryKey: ["game-rooms"] });
      setLocation(`/game/${id}`);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message ?? "Failed to join game",
        variant: "destructive",
      });
    }
  };

  const joinQueue = async () => {
    if (!token) {
      toast({
        title: "Authentication Error",
        description: "Not logged in. Please refresh the page.",
        variant: "destructive",
      });
      return;
    }

    if (isInQueue) {
      toast({
        title: "Already in Queue",
        description: "You are already in the matchmaking queue",
        variant: "destructive",
      });
      return;
    }

    // Use the centralized WebSocket service
    try {
      console.log(
        "[Matchmaking] Joining queue using centralized WebSocket service",
      );
      setIsSearching(true);

      // Use the WebSocket service to join the queue
      webSocketService.joinQueue();

      // The queue state will be updated by the WebSocket service's message handler
    } catch (error) {
      console.error("[Matchmaking] Error joining queue:", error);
      setIsSearching(false);
      toast({
        title: "Error",
        description: "Failed to join matchmaking queue",
        variant: "destructive",
      });
    }
  };

  const leaveQueue = () => {
    console.log(
      "[Matchmaking] Leaving queue using centralized WebSocket service",
    );
    setIsSearching(false);

    // Use the global WebSocket service
    webSocketService.leaveQueue();
  };

  if (!user) {
    return (
      <AppLayout>
        
        <div className="flex flex-col items-center justify-center h-[80vh] gap-6">
          <div className="text-xl font-semibold">
            Please log in to view games
          </div>
          <Button onClick={() => setLocation("/auth")}>Log In</Button>
        </div>
      </AppLayout>
    );
  }

  if (isLoading) {
    return (
      <AppLayout >
        <div className="flex flex-col items-center justify-center min-h-[80vh]">
          <div className="relative w-16 h-16">
            <Loader2 className="h-16 w-16 animate-spin text-primary/40" />
            <Swords className="h-8 w-8 text-primary absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
          </div>
          <p className="mt-4 text-sm text-white/60">Loading games...</p>
        </div>
      </AppLayout>
    );
  }

  // Debug effect removed - Match dialog functionality has been moved to the global MatchDialog component

  const handleRefresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ["game-rooms"] });
    await queryClient.invalidateQueries({ queryKey: ["matchHistory"] });
    await queryClient.invalidateQueries({ queryKey: ["profile-xp"], userId: user?.id });
    await queryClient.invalidateQueries({ queryKey: ["player-awards-pending"] });
  };

  const handleReplayMatch = async (matchId: number) => {
    try {
      const res = await apiRequest("POST", `/api/game-rooms/${matchId}/replay`);
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message ?? "Failed to replay match");
      }
      const data = await res.json();
      setLocation(`/game/${data.id}`);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message ?? "Failed to replay match",
        variant: "destructive",
      });
    }
  };

  // Apply filtering to matches
  const filteredMatches = userMatches?.filter((match: any) => {
    // Search by opponent name or match ID
    const player1Name = match.player1Info?.username?.toLowerCase() || "";
    const player2Name = match.player2Info?.username?.toLowerCase() || "";
    const matchId = match.id.toString();
    const searchTerm = matchSearchTerm.toLowerCase();

    const matchesSearchTerm =
      matchSearchTerm === "" ||
      matchId.includes(searchTerm) ||
      player1Name.includes(searchTerm) ||
      player2Name.includes(searchTerm);

    // Game type filter
    const matchesTypeFilter =
      matchTypeFilter === "all" || match.gameType === matchTypeFilter;

    // Result filter - if current user is winner or not
    let matchesResultFilter = true;
    if (matchResultFilter !== "all" && user) {
      const userWon = match.winner === user.id;
      
      if (matchResultFilter === "won") {
        matchesResultFilter = userWon;
      } else if (matchResultFilter === "lost") {
        matchesResultFilter = !userWon;
      }
    }

    return matchesSearchTerm && matchesTypeFilter && matchesResultFilter;
  }) || [];

  return (
      <AppLayout>
        {/* Enhanced header */}
        <EnhancedHeader
          title="Spar Legends"
          showBackButton={false}
          backPath="/"
          coloredText={true}
          rightContent={
            <div className="flex items-center gap-0">
              
              {(user?.role === 'admin' || user?.role === 'manager') && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="rounded-full bg-black/80 hover:bg-black/60 text-white w-10 h-10"
                  onClick={() => setLocation("/manage")}
                >
                  <ShieldCheck className="h-5 w-5" />
                </Button>
              )}
              {user?.role === 'admin' && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="rounded-full bg-black/80 hover:bg-black/60 text-white w-10 h-10"
                  onClick={() => setLocation("/admin")}
                >
                  <Cog className="h-5 w-5" />
                </Button>
              )}
              <NotificationBell className="relative" />
              <div className="relative">
                <Button
                  variant="ghost"
                  size="icon"
                  className="rounded-full bg-black/80 hover:bg-black/60 text-white w-10 h-10"
                  title="Awards"
                  onClick={() => setIsAwardDialogOpen(true)}
                >
                  <Award className="h-5 w-5" />
                </Button>
                {hasUnclaimed && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center text-[10px] font-bold text-white">
                    !
                  </span>
                )}
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="rounded-full bg-black/80 hover:bg-black/60 text-white w-10 h-10"
                title="Rewards"
                onClick={() => setLocation("/rewards")}
              >
                <Trophy className="h-5 w-5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="rounded-full bg-black/80 hover:bg-black/60 text-white w-10 h-10"
                title="Game Settings"
                onClick={() => setLocation("/settings")}
              >
                <Backpack className="h-5 w-5" />
              </Button>
              {!isDailyRewardClaimed() && <DailyReward/>}
            </div>
          }
          showScrollEffect={true}
          
        />

        
      <PullToRefresh onRefresh={handleRefresh}>

        {/* Announcement Banner - displays important announcements to users */}
        <AnnouncementBanner />


      <div>

        {/* Game notification system */}
        <GameNotifications
          notifications={notifications}
          removeNotification={removeNotification}
        />

        {/* Achievements Dialog - Shows newly unlocked achievements */}
        <AchievementsDialog 
          achievements={newAchievements}
          isOpen={showDialog}
          onClose={closeDialog}
        />
        
        {/* Award Notification Dialog - Shows pending awards */}
        <AwardNotificationDialog 
          open={isAwardDialogOpen}
          onOpenChange={setIsAwardDialogOpen}
          awards={pendingAwards ?? []}
          onClaimAll={handleClaimAll}
        />

        {/* Match dialog has been moved to global MatchDialog component in App.tsx */}

        <div className="container mx-auto px-4 py-2 min-h-[80vh]">

          <div className="flex mb-2 justify-between items-center">
            <span className="text-white text-sm font-medium">
              {userWelcome}
              </span>            
           <span className="flex gap-2 justify-end"> <CurrencyDisplay variant="icon" size="sm" /></span> 
          </div>

          {/* Tab Navigation */}
          <div className="mb-6">
            <div className="inline-flex h-12 items-center justify-center rounded-xl theme-bg-card p-1">
              <button
                onClick={() => setSelectedTab("play")}
                className={`inline-flex items-center justify-center whitespace-nowrap rounded-lg px-4 py-2 text-sm font-semibold transition-all duration-200 ease-out ${
                  selectedTab === "play"
                    ? "theme-bg-primary theme-text-contrast shadow-lg"
                    : "hover:theme-text-primary hover:bg-white/10"
                }`}
              >
                <PlayIcon className="h-4 w-4 mr-2" />
                Play
              </button>
              <button
                onClick={() => setSelectedTab("games")}
                className={`inline-flex items-center justify-center whitespace-nowrap rounded-lg px-4 py-2 text-sm font-semibold transition-all duration-200 ease-out ${
                  selectedTab === "games"
                    ? "theme-bg-primary theme-text-contrast shadow-lg"
                    : "hover:theme-text-primary hover:bg-white/10"
                }`}
              >
                <GamepadIcon className="h-4 w-4 mr-2" />
                Games
              </button>
              <button
                onClick={() => setSelectedTab("history")}
                className={`inline-flex items-center justify-center whitespace-nowrap rounded-lg px-4 py-2 text-sm font-semibold transition-all duration-200 ease-out ${
                  selectedTab === "history"
                    ? "theme-bg-primary theme-text-contrast shadow-lg"
                    : "hover:theme-text-primary hover:bg-white/10"
                }`}
              >
                <ClockIcon className="h-4 w-4 mr-2" />
                Matches
              </button>
              <button
                onClick={() => setSelectedTab("learn")}
                className={`inline-flex items-center justify-center whitespace-nowrap rounded-lg px-4 py-2 text-sm font-semibold transition-all duration-200 ease-out ${
                  selectedTab === "learn"
                    ? "theme-bg-primary theme-text-contrast shadow-lg"
                    : "hover:theme-text-primary hover:bg-white/10"
                }`}
              >
                <BookOpen className="h-4 w-4 mr-2" />
                Learn
              </button>
              
            </div>
          </div>

          <AnimatePresence mode="wait">
            {selectedTab === "play" && (
              <motion.div
                key="play"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.2 }}
                className="space-y-6"
              >
                {/* Removed redundant search indicator since we now have a global floating indicator */}

                {/* Play Tab Content - Game Functionality Icons */}
                 <div className="space-y-5">
                  {/* Match Cards Row */}
                  <div className="grid grid-cols-2 md:grid-cols-2 gap-4">
                    {/* Quick Match Card */}
                    <Card
                      className={`p-5 ${
                        isInQuickQueue 
                          ? "bg-gradient-to-br from-black/60 to-yellow-500/20 border-yellow-500/30" 
                          : isInQueue
                          ? "bg-gradient-to-br from-black/60 to-yellow-500/5 border-yellow-500/10 opacity-50 cursor-not-allowed"
                          : "bg-gradient-to-br from-black/60 to-yellow-500/10 hover:to-yellow-500/20 border-yellow-500/20"
                      } transition-all duration-300 flex flex-col items-center justify-center text-center shadow-lg rounded-xl`}
                      onClick={() => {
                        if (isInQueue && !isInQuickQueue) return; // Prevent clicking if in ranked queue
                        
                        // Play sound when clicked
                        playCardSound();
                        
                        if (isInQuickQueue) {
                          webSocketService.leaveQueue();
                          
                          setIsInQuickQueue(false);
                          // Only reset searching state for quick match
                          setIsSearching(false);
                          return;
                        }
                        
                        // Immediately set searching and queue state for visual feedback
                        setIsInQuickQueue(true);
                        setIsSearching(true);


                        if (wsRef.current?.readyState === WebSocket.OPEN) {
                          wsRef.current.send(JSON.stringify({
                            type: "JOIN_QUICK_QUEUE",
                            token: token,
                          }));
                          
                          
                        }
                      }}
                    >
                      <div className="bg-yellow-500/10 p-6 rounded-full mb-4 shadow-inner">
                        {isInQuickQueue ? (
                          <Loader2 className="h-12 w-12 text-yellow-500 animate-spin" />
                        ) : (
                          <Users className="h-12 w-12 text-yellow-500" />
                        )}
                      </div>
                      <h2 className="font-bold text-2xl text-yellow-500 mb-2">
                        {isInQuickQueue ? "Finding Match..." : "Quick Match"}
                      </h2>
                      <p className="text-sm text-yellow-500/80 max-w-xs">
                        {isInQuickQueue ? "Click to cancel matchmaking" : "Jump into a quick match"}
                      </p>
                    </Card>

                    {/* Ranked Match Card */}
                    <Card
                      className={`p-5 ${
                        user?.role === 'admin' 
                          ? isInQueue 
                            ? "bg-gradient-to-br from-black/60 to-primary/20 border-primary/30" 
                            : "bg-gradient-to-br from-black/60 to-primary/10 hover:to-primary/20 border-primary/20 cursor-pointer"
                          : "bg-gradient-to-br from-black/60 to-gray-500/10 border-gray-500/20 opacity-50 cursor-not-allowed pointer-events-none touch-none"
                      } transition-all duration-300 flex flex-col items-center justify-center text-center shadow-lg rounded-xl`}
                      onClick={() => {
                        // Only allow admins to use ranked
                        if (user?.role !== 'admin') return;
                        
                        // Play sound when clicked
                        playCardSound();
                        
                        if (isInQueue) {
                          leaveQueue();
                          return;
                        }
                        
                        joinQueue();
                      }}
                    >
                      <div className={`${user?.role === 'admin' ? "bg-primary/10" : "bg-gray-500/10"} p-6 rounded-full mb-4 shadow-inner`}>
                        {isInQueue ? (
                          <Loader2 className={`h-12 w-12 ${user?.role === 'admin' ? "text-primary" : "text-gray-500"} animate-spin`} />
                        ) : (
                          <Shield className={`h-12 w-12 ${user?.role === 'admin' ? "text-primary" : "text-gray-500"}`} />
                        )}
                      </div>
                      <h2 className={`font-bold text-2xl ${user?.role === 'admin' ? "text-primary" : "text-gray-500"} mb-2`}>
                        {isInQueue ? "Finding Match..." : "Ranked"}
                      </h2>
                      <p className={`text-sm ${user?.role === 'admin' ? "text-primary/80" : "text-gray-500/80"} max-w-xs`}>
                        {isInQueue 
                          ? "Click to cancel matchmaking" 
                          : user?.role === 'admin' 
                            ? "Admin-only ranked mode" 
                            : "Coming Soon..."
                        }
                      </p>
                    </Card>
                  </div>
                  
                  {/* Secondary Game Options */}
                  <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-4">
                    {/* Practice */}
                    <Card
                      className="relative overflow-hidden border-gray-800 bg-gradient-to-br from-black/80 to-primary/5 hover:to-primary/10 hover:shadow-md transition-all duration-300 p-5 cursor-pointer flex flex-col items-center justify-center text-center"
                      onClick={async () => {
                        // Play sound when clicked
                        playCardSound();

                        // Set loading state
                        setIsPracticeLoading(true);

                        try {
                          // First check if the user has any existing AI games in progress
                          const existingGameResponse = await apiRequest(
                            "GET",
                            `/api/game-rooms/practice/active`,
                          );
                          const existingGames = await existingGameResponse.json();

                          // If there's an existing unfinished AI game, join it instead of creating a new one
                          if (existingGames.match) {
                              setLocation(`/game/${existingGames.match.id}`);
                              toast({
                                title: "Existing game found",
                                description: "Resuming your practice game in progress",
                                variant: "default",
                              });
                          } else {
                            // Create a new practice game
                            const res = await apiRequest(
                              "POST",
                              "/api/game-rooms/practice",
                              {
                                difficulty: gameSettings.aiDifficulty.default,
                              },
                            );
                            
                            if (!res.ok)
                              throw new Error("Failed to create practice game");
                              
                            const data = await res.json();
                            setLocation(`/game/${data.id}`);
                          }
                        } catch (error: any) {
                          console.error("Practice game creation error:", error);
                          toast({
                            title: "Error",
                            description:
                              error.message ??
                              "Failed to create practice game. Please try again.",
                            variant: "destructive",
                          });
                          // Reset loading state on error
                          setIsPracticeLoading(false);
                        }
                      }}
                    >
                      <div className="theme-bg-primary/10 p-4 rounded-full mb-3 shadow-inner">
                        {isPracticeLoading ? (
                          <Loader2 className="h-7 w-7 theme-text-primary animate-spin" />
                        ) : (
                          <Monitor className="h-7 w-7 theme-text-primary" />
                        )}
                      </div>
                      <h3 className="font-medium text-base">
                        {isPracticeLoading ? "Loading..." : "Practice"}
                      </h3>
                    </Card>

                    {/* Create Game */}
                    <Card
                      className="relative overflow-hidden border-gray-800 bg-gradient-to-br from-black/80 to-primary/5 hover:to-primary/10 hover:shadow-md transition-all duration-300 p-5 cursor-pointer flex flex-col items-center justify-center text-center"
                      onClick={() => {
                        // Play sound when clicked
                        playCardSound();
                        
                        if (!user?.isVerified) {
                          toast({
                            title: "Verification Required",
                            description:
                              "You need to verify your email address to create games.",
                            variant: "destructive",
                            action: (
                              <ToastAction
                                altText="Verify"
                                onClick={() => setLocation("/user-settings")}
                              >
                                Verify
                              </ToastAction>
                            ),
                          });
                          return;
                        }
                        setIsCreateModalOpen(true);
                      }}
                    >
                      <div className="theme-bg-primary/10 p-4 rounded-full mb-3 shadow-inner">
                        <Plus className="h-7 w-7 theme-text-primary" />
                      </div>
                      <h3 className="font-medium text-base">Custom Game</h3>
                    </Card>

                    {/* Join Game */}
                    <Card
                      className="relative overflow-hidden border-gray-800 bg-gradient-to-br from-black/80 to-primary/5 hover:to-primary/10 hover:shadow-md transition-all duration-300 p-5 cursor-pointer flex flex-col items-center justify-center text-center"
                      onClick={() => {
                        // Play sound when clicked
                        playCardSound();
                        
                        if (!user?.isVerified) {
                          toast({
                            title: "Verification Required",
                            description:
                              "You need to verify your email address to join games.",
                            variant: "destructive",
                            action: (
                              <ToastAction
                                altText="Verify"
                                onClick={() => setLocation("/user-settings")}
                              >
                                Verify
                              </ToastAction>
                            ),
                          });
                          return;
                        }

                        const joinGameDialog =
                          document.getElementById("join-game-dialog");
                        if (joinGameDialog) {
                          (joinGameDialog as any).showModal();
                        } else {
                          // Create dialog if it doesn't exist
                          const dialog = document.createElement("dialog");
                          dialog.id = "join-game-dialog";
                          dialog.className =
                            "bg-black/95 p-6 rounded-xl shadow-2xl max-w-md w-full mx-auto fixed top-20 left-1/2 transform -translate-x-1/2 z-50 border border-gray-700 text-gray-200";

                          dialog.innerHTML = `
                            <h2 class="text-xl font-bold mb-4 text-white">Join Game</h2>
                            <div class="mb-6">
                              <label class="block text-sm font-medium text-gray-300 mb-2">Game ID</label>
                              <input type="text" id="game-id-input" class="w-full p-3 border border-gray-700 bg-black/30 text-white rounded-lg focus:border-primary/50 focus:ring-1 focus:ring-primary/50 outline-none" placeholder="Enter Game ID">
                            </div>
                            <div class="flex justify-end gap-3">
                              <button id="cancel-join" class="px-4 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-200 transition-colors">Cancel</button>
                              <button id="confirm-join" class="px-4 py-2 rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground transition-colors">Join</button>
                            </div>
                          `;

                          document.body.appendChild(dialog);

                          const gameIdInput =
                            document.getElementById("game-id-input");
                          const cancelButton =
                            document.getElementById("cancel-join");
                          const confirmButton =
                            document.getElementById("confirm-join");

                          cancelButton?.addEventListener("click", () => {
                            dialog.close();
                          });

                          confirmButton?.addEventListener("click", () => {
                            const inputValue = (gameIdInput as HTMLInputElement)
                              ?.value;
                            if (inputValue) {
                              handleJoinGame(inputValue);
                            }
                            dialog.close();
                          });

                          gameIdInput?.addEventListener("keypress", (e) => {
                            if (e.key === "Enter") {
                              const inputValue = (gameIdInput as HTMLInputElement)
                                ?.value;
                              if (inputValue) {
                                handleJoinGame(inputValue);
                              }
                              dialog.close();
                            }
                          });

                          dialog.showModal();
                        }
                      }}
                    >
                      <div className="theme-bg-primary/10 p-4 rounded-full mb-3 shadow-inner">
                        <LogIn className="h-7 w-7 theme-text-primary" />
                      </div>
                      <h3 className="font-medium text-base">Join Game</h3>
                    </Card>

                    {/* Tournaments */}
                    <Card
                      className="relative overflow-hidden border-gray-800 bg-gradient-to-br from-black/80 to-primary/5 hover:to-primary/10 hover:shadow-md transition-all duration-300 p-5 cursor-pointer flex flex-col items-center justify-center text-center"
                      onClick={() => {
                        // Play sound when clicked
                        playCardSound();
                        
                        if (!user?.isVerified) {
                          toast({
                            title: "Verification Required",
                            description:
                              "You need to verify your email address to participate in tournaments.",
                            variant: "destructive",
                            action: (
                              <ToastAction
                                altText="Verify"
                                onClick={() => setLocation("/user-settings")}
                              >
                                Verify
                              </ToastAction>
                            ),
                          });
                          return;
                        }
                        setLocation("/tournaments");
                      }}
                    >
                      <div className="theme-bg-primary/10 p-4 rounded-full mb-3 shadow-inner">
                        <svg
                          className="h-7 w-7 theme-text-primary"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M2 5H22V8H19.5L17 17H12V19H14V21H10V19H12V17H7L4.5 8H2V5Z"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                      <h3 className="font-medium text-base">Tournaments</h3>
                    </Card>
                  </div>

                  
                  
                  {/* Last Match Section */}
                  {userMatches && userMatches.length > 0 && (
                    <div className="mt-6">
                      <MatchCard
                        match={userMatches[0]}
                        onReplayMatch={handleReplayMatch}
                        showHeader={true}
                      />
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            

            {selectedTab === "games" && (
              <motion.div
                key="games"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.2 }}
                className="space-y-4"
              >
                {/* Games Tab Content - Game Listings */}
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <Select value={filter} onValueChange={setFilter}>
                      <SelectTrigger className="w-full theme-bg-secondary/30 border-0 rounded-xl theme-text">
                        <SelectValue placeholder="Filter games" />
                      </SelectTrigger>
                      <SelectContent className="theme-bg-card theme-border">
                        <SelectItem value="all">All Games</SelectItem>
                        <SelectItem value="public">Public</SelectItem>
                        <SelectItem value="quick">Quick Match</SelectItem>
                        <SelectItem value="friends">Friends</SelectItem>
                        <SelectItem value="private">Private</SelectItem>
                        <SelectItem value="ranked">Ranked</SelectItem>
                        <SelectItem value="my-games">My Games</SelectItem>
                          </SelectContent>
                    </Select>
                    <Input
                      placeholder="Search games..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full theme-bg-secondary/30 theme-border-transparent rounded-xl theme-text placeholder:theme-text-muted"
                    />
                  </div>
                  {gameRooms
                    ?.filter((room: any) => {
                      if (filter === "my-games")
                        return room.creator === user?.id && !room.isDeleted;
                      if (filter !== "all" && room.gameType !== filter)
                        return false;
                      return (
                        room.name
                          .toLowerCase()
                          .includes(searchTerm.toLowerCase()) ??
                        room.player1Info?.username
                          .toLowerCase()
                          .includes(searchTerm.toLowerCase()) ??
                        (room.player2Info?.username ?? "")
                          .toLowerCase()
                          .includes(searchTerm.toLowerCase())
                      );
                    })
                    .map(
                      (
                        room: GameRoom & {
                          player1Info: { username: string };
                          player2Info: { username: string };
                        },
                      ) => (
                        <Card
                          key={room.id}
                          className="p-4 hover:bg-accent/50 transition-colors theme-card"
                        >
                          <div className="flex flex-col space-y-4">
                            {/* Header with Game Name and ID */}
                            <div className="flex justify-between items-center">
                              <h3 className="text-lg font-semibold">
                                {room.name}
                              </h3>

                              {/* Game Info Badges */}
                              <span className="flex items-center gap-3">
                                {room.spectatorMode === "public" && (
                                  <Badge
                                    variant="outline"
                                    className="h-6"
                                    title="Public Spectator Mode - Anyone Can Watch"
                                  >
                                    <Tv className="h-3 w-3" />
                                  </Badge>
                                )}
                                {room.spectatorMode === "friends" && (
                                  <Badge
                                    variant="outline"
                                    className="h-6"
                                    title="Friends Only Spectator Mode"
                                  >
                                    <Users className="h-3 w-3" />
                                  </Badge>
                                )}
                                <Badge variant="secondary" className="h-6">
                                  <Users className="h-3 w-3 mr-1" />
                                  {(room.player1 ? 1 : 0) +
                                    (room.player2 ? 1 : 0)}
                                  /2
                                </Badge>
                              </span>
                            </div>
                            <Separator />
                            {/* Players and Join Button */}
                            <div className="flex justify-between items-center">
                              <div className="grid grid-cols-2 gap-4 text-sm">
                                <div className="space-y-1">
                                  <span className="text-muted-foreground">
                                    Host
                                  </span>
                                  <div className="font-medium">
                                    {room.player1Info?.username}
                                  </div>
                                </div>
                                <div className="space-y-1">
                                  <span className="text-muted-foreground">
                                    Opponent
                                  </span>
                                  <div className="font-medium">
                                    {room.player2Info?.username ?? "Waiting..."}
                                  </div>
                                </div>
                              </div>
                              <div className="flex gap-2">
                                {room.creator === user?.id && (
                                  <Button
                                    variant="destructive"
                                    size="sm"
                                    className="h-8"
                                    onClick={async () => {
                                      try {
                                        const res = await apiRequest(
                                          "DELETE",
                                          `/api/game-rooms/${room.id}/soft-delete`,
                                        );
                                        if (!res.ok)
                                          throw new Error(
                                            "Failed to delete game",
                                          );
                                        toast({
                                          title: "Success",
                                          description:
                                            "Game deleted successfully",
                                          variant: "default",
                                        });
                                        queryClient.invalidateQueries({
                                          queryKey: ["game-rooms"],
                                        });
                                      } catch (error: any) {
                                        toast({
                                          title: "Error",
                                          description: error.message ?? "Failed to delete game",
                                          variant: "destructive",
                                        });
                                      }
                                    }}
                                  >
                                    X
                                  </Button>
                                )}
                                <Button
                                  onClick={() =>
                                    handleJoinGame(room.id.toString())
                                  }
                                  className="h-8 gap-2"
                                  variant="default"
                                >
                                  <LogIn className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                            <span>
                              <Badge variant="outline">Game ID: {room.id}</Badge>{" "}
                              <Badge variant="outline">
                                {new Date(room.createdAt).toLocaleDateString()}
                              </Badge>
                            </span>
                          </div>
                        </Card>
                      ),
                    )}
                </div>
              </motion.div>
            )}

            {selectedTab === "learn" && (
              <motion.div
                key="learn"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.2 }}
                className="space-y-6"
              >
                <div>
                  <LearnTabContent />
                </div>
              </motion.div>
            )}

            {selectedTab === "history" && (
              <motion.div
                key="history"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.2 }}
                className="space-y-4"
              >
                <div className="space-y-4 pb-20">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-2">
                      <Clock className="h-5 w-5 text-primary" />
                      <h2 className="text-xl font-bold theme-text-primary">
                        Your Match History
                      </h2>
                    </div>
                  </div>

                  {isLoadingMatches ? (
                    <div className="flex flex-col items-center justify-center py-12">
                      <div className="relative w-16 h-16">
                        <Loader2 className="h-16 w-16 animate-spin text-primary/40" />
                        <Swords className="h-8 w-8 text-primary absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
                      </div>
                      <p className="mt-4 text-sm text-white/60">
                        Loading match history...
                      </p>
                    </div>
                  ) : userMatches && userMatches.length > 0 ? (
                    <div className="space-y-4">
                      {/* Filters section */}
                      <div className="flex flex-col space-y-3 mb-4">
                        <div className="flex justify-between items-center mb-1">
                          <h3 className="text-sm font-medium text-white/70">Filters</h3>
                          <Badge variant="outline" className="bg-black/40 text-white/70 text-xs">
                            {filteredMatches.length} of {userMatches.length} matches
                          </Badge>
                        </div>
                        <div className="relative">
                          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-white/40" />
                          <input
                            type="text"
                            placeholder="Search by opponent or match ID..."
                            className="w-full rounded-md bg-black/20 border border-white/10 pl-9 pr-4 py-2 text-sm text-white placeholder:text-white/40"
                            value={matchSearchTerm}
                            onChange={(e) => setMatchSearchTerm(e.target.value)}
                          />
                        </div>
                        
                        <div className="flex flex-wrap gap-2">
                          <select
                            className="rounded-md bg-black/20 border border-white/10 px-3 py-1.5 text-sm text-white"
                            value={matchTypeFilter}
                            onChange={(e) => setMatchTypeFilter(e.target.value)}
                          >
                            <option value="all">All Match Types</option>
                            <option value="ranked">Ranked</option>
                            <option value="quick">Quick Match</option>
                            <option value="tournament">Tournament</option>
                          </select>
                          
                          <select
                            className="rounded-md bg-black/20 border border-white/10 px-3 py-1.5 text-sm text-white"
                            value={matchResultFilter}
                            onChange={(e) => setMatchResultFilter(e.target.value)}
                          >
                            <option value="all">All Results</option>
                            <option value="won">Wins</option>
                            <option value="lost">Losses</option>
                          </select>
                        </div>
                      </div>
                      
                      {filteredMatches.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-6 text-white/50 border border-white/10 rounded-lg">
                          <Swords className="w-10 h-10 opacity-20 mb-2" />
                          <p className="text-center">No matches found</p>
                          {(matchSearchTerm || matchTypeFilter !== "all" || matchResultFilter !== "all") && (
                            <p className="text-xs mt-2 text-white/40">Try adjusting your filters</p>
                          )}
                        </div>
                      ) : (
                        <motion.div
                          className="space-y-4 pt-2"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ staggerChildren: 0.05 }}
                        >
                          {filteredMatches.map((match: any) => (
                            <motion.div
                              key={match.id}
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              whileHover={{ scale: 1.01 }}
                              transition={{ duration: 0.2 }}
                            >
                              <MatchCard
                                match={match}
                                onReplayMatch={handleReplayMatch}
                              />
                            </motion.div>
                          ))}
                        </motion.div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-12 theme-border border-dashed rounded-xl border">
                      <Swords className="h-12 w-12 mx-auto mb-4 text-white/30" />
                      <p className="text-lg font-medium text-white/80">
                        No match history yet
                      </p>
                      <p className="text-sm text-white/50 mt-1">
                        Play some games to see your history here
                      </p>
                      <Button
                        className="mt-4"
                        onClick={() => setSelectedTab("play")}
                      >
                        Play Now
                      </Button>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <AnimatePresence>
          {showHowToPlay && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
              onClick={() => setShowHowToPlay(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="max-w-2xl w-full"
                onClick={(e) => e.stopPropagation()}
              >
                <HowToPlay />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <CreateGameDialog 
          isOpen={isCreateModalOpen} 
          onOpenChange={setIsCreateModalOpen} 
        />
        </div>
            </PullToRefresh>

      </AppLayout>
  );
}

// Helper function to get WebSocket protocol
function getWebSocketProtocol(url: string) {
  return (new URL(url)).protocol === "https:" ? "wss:" : "ws:";
}
